// Helper único para lidar com papéis de usuário (userType).
//
// Contexto: historicamente `userType` ora era string ("employee"), ora array
// (["b2c","affiliates"]). Com papéis acumuláveis (uma pessoa pode ser cliente E
// afiliado E colaborador na mesma conta), o campo passa a ser sempre tratado
// como um conjunto de papéis. Estas funções centralizam a normalização para que
// nenhuma tela precise reescrever a lógica string|array.

// Ordem de prioridade para escolher o papel "principal" (roteamento padrão de
// login). Mais específico/privilegiado primeiro.
const ROLE_PRIORITY = [
  "master",
  "employee",
  "manager",
  "formatter",
  "linguist",
  "reviewer",
  "translator",
  "b2b",
  "b2c",
  "colab",
  "affiliates",
];

// Converte qualquer forma de `userType` (string, array, null) em um array de
// papéis minúsculos, sem duplicatas e sem vazios.
export const normalizeRoles = (userType) => {
  const raw = Array.isArray(userType) ? userType : [userType];
  const out = [];
  for (const r of raw) {
    if (typeof r !== "string") continue;
    const v = r.trim().toLowerCase();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
};

// Verifica se o usuário tem um papel específico.
export const hasRole = (userType, role) =>
  normalizeRoles(userType).includes(String(role || "").toLowerCase());

// Retorna um novo array de papéis com `role` adicionado (idempotente).
export const addRole = (userType, role) => {
  const roles = normalizeRoles(userType);
  const v = String(role || "").trim().toLowerCase();
  if (v && !roles.includes(v)) roles.push(v);
  return roles;
};

// Escolhe o papel principal segundo a prioridade; cai no primeiro disponível.
export const primaryRole = (userType) => {
  const roles = normalizeRoles(userType);
  for (const r of ROLE_PRIORITY) if (roles.includes(r)) return r;
  return roles[0] || null;
};

// Papéis de "cliente" (quem cria/gerencia projetos como contratante).
export const CLIENT_ROLES = ["b2b", "b2c", "colab"];

// Em CONTEXTO de cliente, escolhe o papel de cliente efetivo (b2b > b2c >
// colab), ignorando papéis como employee/affiliates/master. Default "b2c".
// Use isto ao derivar um `userType` string que será comparado com
// === "b2b"/"b2c"/"colab" — assim contas multi-papel não quebram a lógica.
export const clientRole = (userType, fallback = "b2c") => {
  const roles = normalizeRoles(userType);
  for (const r of CLIENT_ROLES) if (roles.includes(r)) return r;
  return fallback;
};

// Override de permissões EFETIVO para um CARGO específico (multi-cargo).
// Prioriza o override por cargo (`user.permsByRole[cargo]`); cai no override
// antigo global (`user.perms`) para compatibilidade; null = herda do cargo.
export const permsForRole = (user, role) => {
  if (role && user?.permsByRole && user.permsByRole[role]) return user.permsByRole[role];
  return user?.perms || null;
};

export { ROLE_PRIORITY };
